import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bell, Check, ChevronsUpDown, Command, Eye, MapPin, Menu as MenuIcon, Moon, Search, Sun, TriangleAlert } from 'lucide-react'
import { ROLES, type Role } from '@/app/nav'
import { useCommand } from '@/app/command'
import { useRole } from '@/app/role'
import { initialsOf, useAuth } from '@/lib/auth/auth'
import { useCurrentSite, useSiteList } from '@/app/sites'
import { getTheme, setTheme, type Theme } from '@/app/theme'
import { ALERTS } from '@/mocks'
import { tok } from '@/charts'
import { cn } from '@/lib/utils'

function Menu({
  open,
  onClose,
  children,
  width = 'w-56',
}: {
  open: boolean
  onClose: () => void
  children: React.ReactNode
  width?: string
}) {
  if (!open) return null
  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} />
      <div className={cn('absolute right-0 z-50 mt-2 rounded-lg border bg-popover p-1.5 shadow-xl', width)}>
        {children}
      </div>
    </>
  )
}

const SEV_TONE: Record<string, string> = { critical: tok('--ignition'), warning: tok('--flare'), info: tok('--clear') }
const age = (m: number) => (m < 60 ? `${m}m` : `${Math.round(m / 60)}h`)

function AlertsBell() {
  const nav = useNavigate()
  const [open, setOpen] = useState(false)
  const unread = ALERTS.length
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label="Alerts"
        aria-haspopup="menu"
        aria-expanded={open}
        className="relative grid size-9 place-items-center rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground"
      >
        <Bell className="size-[18px]" />
        {unread > 0 && <span className="absolute right-2 top-2 size-[7px] rounded-full bg-ignition ring-2 ring-background" />}
      </button>
      <Menu open={open} onClose={() => setOpen(false)} width="w-[320px]">
        <div className="flex items-center justify-between px-2 py-1.5">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-faint">Alerts</span>
          <span className="text-[11px] text-faint">{unread} unread</span>
        </div>
        {ALERTS.slice(0, 4).map((a) => (
          <button
            key={a.id}
            onClick={() => {
              setOpen(false)
              nav('/alerts')
            }}
            className="flex w-full items-start gap-2 rounded-md px-2 py-2 text-left hover:bg-secondary"
          >
            <TriangleAlert className="mt-0.5 size-3.5 shrink-0" style={{ color: SEV_TONE[a.severity] }} />
            <span className="min-w-0 flex-1">
              <span className="block text-[12.5px] leading-snug">{a.message}</span>
              <span className="block text-[11px] text-faint">{a.site}</span>
            </span>
            <span className="shrink-0 text-[10.5px] text-faint">{age(a.ageMins)}</span>
          </button>
        ))}
        <button
          onClick={() => {
            setOpen(false)
            nav('/alerts')
          }}
          className="mt-1 w-full border-t px-2 py-2 text-center text-[12.5px] font-semibold text-primary hover:bg-secondary"
        >
          View all alerts
        </button>
      </Menu>
    </div>
  )
}

function SiteSwitcher() {
  const nav = useNavigate()
  const [open, setOpen] = useState(false)
  const { site } = useCurrentSite()
  const { sites } = useSiteList()
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 rounded-md border border-input bg-surface px-2.5 py-1.5 text-[13.5px] font-semibold hover:bg-surface-2"
      >
        <MapPin className="size-[15px] text-faint" /> {site?.name ?? 'Select site'}
        <ChevronsUpDown className="size-[15px] text-faint" />
      </button>
      <Menu open={open} onClose={() => setOpen(false)}>
        <div className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-faint">Sites</div>
        {sites.map((s) => (
          <button
            key={s.id}
            onClick={() => {
              setOpen(false)
              nav(`/site/${s.id}`)
            }}
            className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[13px] hover:bg-secondary"
          >
            <span className="truncate">{s.name}</span>
            <span
              className={cn(
                'ml-auto rounded px-1.5 py-px font-display text-[10px] font-semibold',
                s.pack === 'BESS' ? 'bg-stable/20 text-clear' : 'bg-ignition/[0.14] text-flare',
              )}
            >
              {s.pack}
            </span>
          </button>
        ))}
      </Menu>
    </div>
  )
}

function RolePicker() {
  const { role, setRole } = useRole()
  const { user } = useAuth()
  const [open, setOpen] = useState(false)
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="grid size-[30px] place-items-center rounded-[9px] bg-gradient-to-br from-stable to-contained font-display text-[11px] font-bold text-white"
        title={user?.name ?? undefined}
      >
        {initialsOf(user?.name ?? user?.email)}
      </button>
      <Menu open={open} onClose={() => setOpen(false)}>
        <div className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-faint">
          Switch role (demo)
        </div>
        {ROLES.map((r: (typeof ROLES)[number]) => (
          <button
            key={r.id}
            onClick={() => {
              setRole(r.id as Role)
              setOpen(false)
            }}
            className={cn(
              'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[13px] hover:bg-secondary',
              r.id === role && 'bg-secondary',
            )}
          >
            <span className="grid size-6 shrink-0 place-items-center rounded-md bg-secondary font-display text-[10px] font-bold text-muted-foreground">
              {r.initials}
            </span>
            <span className="min-w-0">
              <span className="block font-medium">{r.name}</span>
              <span className="block text-[11px] text-faint">{r.blurb}</span>
            </span>
            {r.id === role && <Check className="ml-auto size-4 text-primary" />}
          </button>
        ))}
      </Menu>
    </div>
  )
}

export function Topbar({ onMenu }: { onMenu?: () => void }) {
  const { role } = useRole()
  const { site } = useCurrentSite()
  const command = useCommand()
  const [theme, setThemeState] = useState<Theme>(getTheme())
  const toggleTheme = () => {
    const next: Theme = theme === 'dark' ? 'light' : 'dark'
    setTheme(next)
    setThemeState(next)
  }
  return (
    <header className="relative z-50 flex h-14 items-center gap-3 border-b bg-background/90 px-5 backdrop-blur max-md:px-3">
      <button
        onClick={onMenu}
        aria-label="Open navigation"
        className="grid size-9 place-items-center rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground md:hidden"
      >
        <MenuIcon className="size-[18px]" />
      </button>
      <SiteSwitcher />
      {site && (
        <span
          className={cn(
            'rounded-md px-2 py-1 font-display text-[11px] font-semibold',
            site.pack === 'BESS' ? 'bg-stable/20 text-clear' : 'bg-ignition/[0.14] text-flare',
          )}
        >
          {site.pack}
        </span>
      )}
      <button
        onClick={command.open}
        className="ml-1 hidden h-9 max-w-[320px] flex-1 items-center gap-2 rounded-md border border-input bg-surface-2 px-3 text-[13px] text-faint hover:bg-surface md:flex"
      >
        <Search className="size-4" /> Search sites, questions, evidence
        <span className="ml-auto inline-flex items-center gap-0.5 rounded border border-input px-1.5 py-px font-mono text-[11px]">
          <Command className="size-3" />K
        </span>
      </button>
      <div className="ml-auto flex items-center gap-1">
        <button
          onClick={command.open}
          aria-label="Search"
          className="grid size-9 place-items-center rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground md:hidden"
        >
          <Search className="size-[18px]" />
        </button>
        {role === 'insurer' && (
          <span className="mr-1 inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-clear">
            <Eye className="size-3.5" /> Read-only
          </span>
        )}
        <button
          onClick={toggleTheme}
          aria-label="Toggle theme"
          className="grid size-9 place-items-center rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground"
        >
          {theme === 'dark' ? <Sun className="size-[18px]" /> : <Moon className="size-[18px]" />}
        </button>
        <AlertsBell />
        <RolePicker />
      </div>
    </header>
  )
}
